import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import Breadcrumbs from "../components/Breadcrumbs";
import IntegrationGrid from "../components/IntegrationGrid";
import { ConfigModal } from "../components/IntegrationDetailPage";
import { integrations, getIntegrationById } from "../data/integrations";

import { useTranslation } from 'react-i18next';
export default function IntegrationsPage() {
  const { t } = useTranslation();
  const [configIntegrationId, setConfigIntegrationId] = useState(null);
  const [connectedIds, setConnectedIds] = useState(
    integrations.filter((i) => i.status === "connected").map((i) => i.id)
  );

  const configIntegration = configIntegrationId
    ? getIntegrationById(configIntegrationId)
    : null;

  const handleConnect = (id) => {
    setConnectedIds((prev) => [...prev, id]);
    toast.success(`${getIntegrationById(id)?.name} connected successfully`);
  };

  const handleDisconnect = (id) => {
    setConnectedIds((prev) => prev.filter((cid) => cid !== id));
    toast.info(`${getIntegrationById(id)?.name} disconnected`);
  };

  const breadcrumbItems = [
    { label: "Home", path: "/" },
    { label: "Marketplace", path: "/marketplace" },
    { label: "Integrations" },
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Max OS Cloud Integration Marketplace",
    description: "Connect your favorite tools with Max OS Cloud",
    itemListElement: integrations.map((i, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: i.name,
      description: i.tagline,
    })),
  };

  return (
    <>
      <SEOHead
        title={t('integrations_page.integration_marketplace_connect_12_tools_max')}
        description={t('integrations_page.connect_stripe_slack_notion_github_hubspot_and')}
        url="/marketplace/integrations"
        structuredData={structuredData}
      />
      <Header />

      <main id="main-content" className="min-h-screen bg-[#09090B]">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" aria-hidden="true" />
          <div
            className="absolute inset-0 opacity-[0.03]"
            aria-hidden="true"
            style={{
              backgroundImage: "radial-gradient(circle, #f59e0b 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-16">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 border border-zinc-800 rounded-full px-4 py-1.5 mb-8">
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-400">
                  {connectedIds.length} connected
                </span>
              </div>
              <h1 className="text-5xl md:text-6xl font-display font-bold text-white leading-[1.1] tracking-tight">{t('integrations_page.integration_marketplace')}</h1>
              <p className="mt-6 text-lg md:text-xl text-zinc-400 leading-relaxed max-w-xl">
                Connect your favorite tools with Max OS Cloud. Payments, CRM,
                productivity, and more — all in one place.
              </p>
            </div>
          </div>
        </section>

        <Breadcrumbs items={breadcrumbItems} />

        {/* Grid section */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
          <IntegrationGrid
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
            onConfigModal={setConfigIntegrationId}
          />
        </section>

        {/* CTA */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-16">
          <div className="border border-amber-500/20 rounded-xl p-12 text-center bg-gradient-to-b from-amber-500/[0.03] to-transparent">
            <h2 className="text-3xl font-display font-bold text-white mb-4">{t('integrations_page.need_a_custom_integration')}</h2>
            <p className="text-zinc-400 mb-8 max-w-lg mx-auto">{t('integrations_page.our_api_supports_webhooks_oauth_and_custom')}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/docs/api"
                className="bg-amber-500 hover:bg-amber-400 text-black px-8 py-3.5 font-bold uppercase tracking-wider text-sm transition-colors"
              >
                View API Docs
              </Link>
              <Link
                to="/contact"
                className="border border-zinc-700 hover:border-zinc-500 text-zinc-300 px-8 py-3.5 font-mono uppercase tracking-wider text-xs transition-colors"
              >
                Contact Sales
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Config Modal */}
      {configIntegration && (
        <ConfigModal
          integration={configIntegration}
          onClose={() => setConfigIntegrationId(null)}
          onConnect={handleConnect}
        />
      )}
    </>
  );
}
